<template>
    <div>
        <div class="poi-modal__body">
            <div class="poi-modal__header">Файл распознан!</div>

            <div class="poi-modal__status">
                <p>Точки в файле <b>{{ file.name }}</b> распознаны. Найдено {{ successCount }}, не найдено {{ errorCount }}.</p>
                <br><p>При повторном ручном поиске точка будет автоматически отнесена к административному центру, если в базе не будет найден населенный пункт.</p>
            </div>

            <div v-if="isComposingAddress" class="poi-modal__status warning">
                <p><b>Внимание:</b> сопоставление по адресу может быть неточным, рекомендуем пользоваться загрузкой по координатам.</p>
            </div>
        </div>

        <div class="poi-modal__tabs">
            <div
                @click="onTabClickAction('success')"
                :class="getTabClassesByTabId('success')"
            >
                Найдено ({{successCount}})
            </div>

            <div
                @click="onTabClickAction('error')"
                :class="getTabClassesByTabId('error')"
            >
                Не найдено ({{errorCount}})
            </div>
        </div>

        <div class="table-container">
            <Table primaryKey="num" :columns="tableColumns" :rows="resultRows">
            <template v-slot:city_name="scopeData">
                <BaseInput
                    v-if="activeTabId === 'error' && fileComposingType ==='address'"
                    class="poi-modal__input"
                    :value="getValue(scopeData.row, 'city_name')"
                    @onInput="editValue(scopeData.row, 'city_name', $event)"
                />
                <p v-else>{{ scopeData.row.city_name }}</p>
            </template>
            <template v-slot:address="scopeData">
                <BaseInput
                    v-if="activeTabId === 'error' && fileComposingType ==='address'"
                    class="poi-modal__input"
                    :value="getValue(scopeData.row, 'address')"
                    @onInput="editValue(scopeData.row, 'address', $event)"
                />
                <p v-else>{{ scopeData.row.address }}</p>
            </template>
            <template v-slot:lat="scopeData">
                <BaseInput
                    v-if="activeTabId === 'error' && fileComposingType ==='coords'"
                    class="poi-modal__input"
                    :value="getValue(scopeData.row, 'lat')"
                    @onInput="editValue(scopeData.row, 'lat', $event)"
                />
                <p v-else>{{ scopeData.row.lat }}</p>
            </template>
            <template v-slot:lng="scopeData">
                <BaseInput
                    v-if="activeTabId === 'error' && fileComposingType ==='coords'"
                    class="poi-modal__input"
                    :value="getValue(scopeData.row, 'lng')"
                    @onInput="editValue(scopeData.row, 'lng', $event)"
                />
                <p v-else>{{ scopeData.row.lng }}</p>
            </template>
            <template v-slot:categories="scopeData">
                <div class="poi-categories">
                    <p
                        v-for="(category, index) in scopeData.row.categories"
                        :key="index"
                        class="poi-categories__item"
                    >
                        {{category}}
                    </p>
                </div>
            </template>
            <template v-slot:actions="scopeData">
                <div
                    class="poi-modal__row-actions"
                >
                    <div
                        v-if="activeTabId === 'success'"
                        v-tooltip="'Переместить в не найдено'"
                        class="poi-modal__row-button"
                        @click="onMoveItem(scopeData.row)"
                    >
                        <LinceIconEdit class="icon" :width="20" :height="20" />
                    </div>

                    <div
                        v-if="activeTabId === 'error'"
                        v-tooltip="'Найти'"
                        class="poi-modal__row-button"
                        @click="onSearchItem(scopeData.row)"
                    >
                        <LinceIconSearch class="icon" :width="20" :height="20" />
                    </div>

                    <div
                        v-if="activeTabId === 'error'"
                        v-tooltip="'Удалить'"
                        class="poi-modal__row-button"
                        @click="onRemoveItem(scopeData.row)"
                    >
                        <LinceIconTrash class="icon" :width="20" :height="20" />
                    </div>
                </div>
            </template>
        </Table>
        </div>

        <div class="poi-modal__actions">
            <LinceButton
                class="poi-modal__cancel"
                :view="'square'"
                :theme="'border'"
                @click="onCloseModal"
            >
                {{ addGroupModal.textCancel }}
            </LinceButton>
            <LinceButton
                v-if="successCount > 0"
                :view="'square'"
                :theme="'secondary'"
                @click="onApply"
            >{{ addGroupModal.textApply }}
            </LinceButton>
        </div>
    </div>
</template>

<script>
import containerPoiSchemes from "../../schemes/SchemeWidgetMap";
import Table from "@/components/Table2/Table";
import BaseInput from "@/components/Base/BaseInput.vue";

const { containerPoi } = containerPoiSchemes;

export default {
    components: {
        Table,
        BaseInput
    },

    props: {
        successPoints: {
            type: Array,
            default: () => [],
        },
        errorPoints: {
            type: Array,
            default: () => [],
        },
        file: {
            type: File,
            default: () => {},
        },
        fileComposingType: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            addGroupModal: containerPoi.addGroupModal,
            tableColumns: containerPoi.uploadedPoiColumns,
            activeTabId: 'success'
        };
    },

    computed: {
        successCount() {
            return this.successPoints.length;
        },

        errorCount() {
            return this.errorPoints.length;
        },

        resultRows() {
            if(this.activeTabId === 'success') return this.successPoints
            else return this.errorPoints
        },

        isComposingAddress() {
            return this.fileComposingType === 'address'
        }
    },

    methods: {
        onCloseModal() {
            this.$emit("onCloseModal");
        },

        onApply() {
            this.$emit("addPoints");
        },

        getValue(row, attribute) {
            return row[attribute] ?? "";
        },

        onSearchItem(row) {
            this.$emit("onSearchItem", row);
        },

        onMoveItem(row) {
            this.$emit("onMoveItem", row);
        },

        onRemoveItem(row) {
            const {num = null} = row

            this.$emit("onRemoveItem", num);
        },

        editValue(row, attribute, value) {
            this.$emit("onEditItem", row, attribute, value);
        },

        getTabClassesByTabId(tabId) {
            return ['poi-modal__tabs--item', { _active: this.activeTabId === tabId }];
        },

        onTabClickAction(id) {
            this.activeTabId = id;
        },
    },
};
</script>

<style lang="scss">
.poi-categories {
    padding: 2px;

    &__item {
        display: inline-block; // 👈 делаем ширину по содержимому
        padding: 3px;
        background-color: #f5f5f5;
        border-radius: 4px;
        width: fit-content; // 👈 на всякий случай
        max-width: 100%; // если вдруг содержимое слишком большое
        white-space: nowrap; // опционально, чтобы текст не переносился
    }
}

.table-container {
    min-height: 250px; /* Минимальная высота, можно настроить */
    max-height: 30vh; /* 60% высоты экрана */
    overflow-y: auto; /* Вертикальный скролл при необходимости */
    margin-bottom: 20px;
    width: 100%;
    max-width: 100%; /* Ограничить ширину */
    display: flex;
    justify-content: center;
}

.table-container .table {
    overflow-x: hidden;
}

.poi-modal {

    &__tabs {
        width: max-content;
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        padding: 9px 10px;
        border-radius: 10px;
        background: #f5f5f5;

        &--item {
            padding: 3px 10px;
            margin-right: 20px;
            border-radius: 4px;
            line-height: 18px;
            font-weight: 400;
            font-size: 14px;
            cursor: pointer;
            transition: all ease 0.1s;

            &:last-child {
                margin-right: 0;
            }

            &:hover {
                background: #eaeced;
            }

            &._active {
                color: #fff;
                background: #4a91f1;
            }
        }
    }

    &__input{
        height: 30px !important;
        width: 100%;
    }

    &__label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 15px;

        cursor: pointer;
    }

    &__data.active {
        .poi-modal__table {
            display: block;
        }
    }

    &__status {
        padding: 20px;
        margin-bottom: 10px;
        font-weight: 400;
        font-size: 14px;
        line-height: 18px;
        border-radius: 15px;
        color: #131314;
        background: #edfae7;
    }

    &__status.warning {
        background: #FAF7E7FF;
    }

    &__table {
        display: none;
        overflow: auto;
        border-collapse: collapse;
        border-spacing: 0;

        & tr {
            border-radius: 4px;
        }
        & th,
        & td {
            padding: 10px;
            text-align: center;
            font-weight: 400;
            font-size: 12px;
            color: #46494e;
            border: 1px solid #d9e1e9;
        }

        & th {
            padding: 10px 30px;
            font-weight: 400;
            font-size: 12px;
            line-height: 14px;
            color: #747e8c;
            background: #eff3f6;
        }
    }
    &__body {
        display: flex;
        flex-direction: column;
        margin-bottom: 30px;
        min-height: max-content;
        background: #fff;
        border-radius: 10px;
        box-sizing: border-box;
    }

    &__header {
        margin-bottom: 20px;
        font-size: 20px;
        font-weight: 500;
        color: #000;

        @media (max-width: 576px) {
            font-size: 14px;
        }
    }

    &__row {
        margin-bottom: 20px;

        &:last-child {
            margin-bottom: 25px;
        }

        &._select {
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
    }

    &__text {
        font-size: 12px;
        line-height: 16px;
        margin-bottom: 5px;
    }
    &__error {
        font-size: 12px;
        line-height: 16px;
        margin-bottom: 5px;
        color: #ff7c8e;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        button {
            margin-right: 10px;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    &__row-actions {
        display: flex;
    }

    &__row-button {
        display: flex;
        justify-content: center;
        align-items: center;

        width: 36px;
        height: 36px;

        border-radius: 4px;
        cursor: pointer;
        transition: all .4s ease-out;
    }

    &__row-button:hover {
        background-color: #f2f2f2;
    }

    &__cancel {
        color: #c8cad1;

        &:hover,
        &:active {
            color: #333;
        }
    }
}
</style>
